import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

export interface ConfirmDialogProps {
  /** Whether the dialog is visible */
  open: boolean;
  /** Dialog heading */
  title: string;
  /** Body text / content */
  message: React.ReactNode;
  /** Confirm button label (default "Confirm") */
  confirmLabel?: string;
  /** Cancel button label (default "Cancel") */
  cancelLabel?: string;
  /** MUI colour for the confirm button (default "primary"; forced to "error" when destructive) */
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /** Show a warning icon + error-coloured confirm button for destructive actions */
  destructive?: boolean;
  /** Disable buttons and show the loading label on confirm while an async action runs */
  loading?: boolean;
  /** Label shown on the confirm button while loading (e.g. "Deleting...") */
  loadingLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Shared confirmation dialog — the project's MUI replacement for native
 * window.confirm(). Per .claude/rules/frontend.md we never use window.confirm /
 * window.alert. Reuse this instead of hand-rolling another per-feature dialog.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor,
  destructive = false,
  loading = false,
  loadingLabel,
  onConfirm,
  onClose,
}) => {
  const resolvedConfirmColor = confirmColor ?? (destructive ? 'error' : 'primary');

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-busy={loading}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {destructive && <WarningIcon color="error" />}
        {title}
      </DialogTitle>
      <DialogContent>
        {typeof message === 'string' ? (
          <DialogContentText>{message}</DialogContentText>
        ) : (
          message
        )}
      </DialogContent>
      <DialogActions>
        {/* For destructive actions focus Cancel by default so a stray Enter doesn't confirm. */}
        <Button onClick={onClose} disabled={loading} autoFocus={destructive}>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color={resolvedConfirmColor}
          variant="contained"
          disabled={loading}
          autoFocus={!destructive}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading && loadingLabel ? loadingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
